import api, { apiSuccessOutput } from '@/shared/config/api.js';
import { APPLICATION_REQUEST_TIMEOUT } from '@/feature/Devices/utils/applicationQuery.js';

// Create Gateway
export const createGateway = async (eui, name) => {
  return apiSuccessOutput(
    await api.post('/device/v1/gateway', {
      eui,
      name,
    }),
  );
};

// Get Gateway wiht paginate
export const getGatewayList = async (page = 1, size = 10) => {
  return apiSuccessOutput(
    await api.get('/device/v1/gateway', {
      params: { page, size },
      showToast: false,
    }),
  );
};

// Get Gateway details
export const getGatewayDetails = async (gateway_id) => {
  return apiSuccessOutput(
    await api.get(`/device/v1/gateway/${gateway_id}`, {
      showToast: false,
    }),
  );
};

// Update Gateway
export const updateGateway = async (gateway_id, name) => {
  return apiSuccessOutput(
    await api.put(`/device/v1/gateway/${gateway_id}`, { name }),
  );
};

// Delete Gateway
export const deleteGateway = async (gateway_id) => {
  return apiSuccessOutput(await api.delete(`/device/v1/gateway/${gateway_id}`));
};

// Create Device From Template
export const createDeviceTemplate = async (
  eui,
  name,
  address,
  template_id,
  application_id,
) => {
  return apiSuccessOutput(
    await api.post('/device/v1/from-template', {
      eui,
      name,
      address,
      template_id,
      application_id,
    }),
  );
};

// Get Device List with paginate
export const getDevicesList = async (page = 1, size = 10) => {
  return apiSuccessOutput(
    await api.get('/device/v1/paginate', {
      params: { page, size },
      showToast: false,
    }),
  );
};

// Update Device Item
export const updateDevice = async (device_id, name) => {
  return apiSuccessOutput(
    await api.put(`/device/v1/${device_id}`, {
      name,
    }),
  );
};

// Delete Device Item
export const deleteDevice = async (device_id) => {
  return apiSuccessOutput(await api.delete(`/device/v1/${device_id}`));
};

// Get Detail of Device
export const getDetailDevice = async (device_id) => {
  return apiSuccessOutput(
    await api.get(`/device/v1/details/${device_id}`, {
      showToast: false,
    }),
  );
};

// Get Application List without paginate
export const getApplicationsList = async ({ tenantId, signal } = {}) => {
  return apiSuccessOutput(
    await api.get('/application/v1', {
      showToast: false,
      tenantId,
      signal,
      timeout: APPLICATION_REQUEST_TIMEOUT,
    }),
  );
};

// Get Profile Template
export const getProfileTemplates = async () => {
  return apiSuccessOutput(
    await api.get('/device/v1/profile-template', {
      showToast: false,
    }),
  );
};

// Check status of Gateway
export const checkStatusGateway = async ({ showToast = true } = {}) => {
  return apiSuccessOutput(
    await api.post('/device/v1/gateway/check-status', null, { showToast }),
  );
};

// Check Status of Devices
export const checkStatusDevice = async ({ showToast = true } = {}) => {
  return apiSuccessOutput(
    await api.post('/device/v1/check-status', null, { showToast }),
  );
};

// Search Device List
export const searchDeviceList = async ({
  name,
  eui,
  status,
  device_profile,
  has_measurement_issue,
  page = 1,
  size = 10,
}) => {
  return apiSuccessOutput(
    await api.get('/device/v1/search', {
      params: {
        name,
        eui,
        status,
        device_profile,
        has_measurement_issue: has_measurement_issue || undefined,
        page,
        size,
      },
      showToast: false,
    }),
  );
};

// Search Gateway List
export const searchGatewayList = async ({
  name,
  eui,
  status,
  page = 1,
  size = 10,
}) => {
  return apiSuccessOutput(
    await api.get('/device/v1/gateway/search', {
      params: {
        name,
        eui,
        status,
        page,
        size,
      },
      showToast: false,
    }),
  );
};

// Get parameter by Eui
export const getParameterByEui = async (eui) => {
  return apiSuccessOutput(
    await api.get(`/device/v1/parameter/per-eui/${eui}`, {
      showToast: false,
    }),
  );
};

// Get All parameter
export const getAllParameterList = async () => {
  return apiSuccessOutput(await api.get('/device/v1/parameter/all'));
};

// ----------------- Tags  -----------------
// Create Tags
export const createTags = async (name) => {
  return apiSuccessOutput(await api.post('/device/v1/tag', { name }));
};

// Get Tags Without Pageination
export const gettagListWithoutPagination = async () => {
  return apiSuccessOutput(await api.get('/device/v1/tag'));
};

export const assignModelTag = async (tagId, templateId, dryRun = true) =>
  apiSuccessOutput(
    await api.post(
      `/device/v1/tag/${tagId}/devices/by-template`,
      { template_id: Number(templateId), dry_run: dryRun },
      { showToast: !dryRun },
    ),
  );

// Get Tags With Pagination
export const getTagList = async (page = 1, size = 10, name) => {
  return apiSuccessOutput(
    await api.get('/device/v1/tag/paginate', {
      params: {
        page,
        size,
        name,
      },
      showToast: false,
    }),
  );
};

// Get Detail Of Tags
export const getTagDetail = async (tagId) => {
  return apiSuccessOutput(
    await api.get(`/device/v1/tag/${tagId}`, {
      showToast: false,
    }),
  );
};

// Update Tag
export const updateTags = async (tagId, name) => {
  return apiSuccessOutput(await api.put(`/device/v1/tag/${tagId}`, { name }));
};

// Delete Tag
export const deleteTag = async (tagId) => {
  return apiSuccessOutput(await api.delete(`/device/v1/tag/${tagId}`));
};

// Assign Devices To Tag
export const assignDeviceTag = async (tagId, deviceIds) => {
  const payload = {
    device_ids: deviceIds,
  };

  return apiSuccessOutput(
    await api.post(`/device/v1/tag/${tagId}/devices`, payload),
  );
};

// Assign Gateway To Tag
export const assignGatewayTag = async (tagId, deviceIds) => {
  const payload = {
    gateway_ids: deviceIds,
  };

  return apiSuccessOutput(
    await api.post(`/device/v1/tag/${tagId}/gateways`, payload),
  );
};

// Delete Device From Tag
export const deleteDeviceTag = async (deviceTagId, tagId) => {
  return apiSuccessOutput(
    await api.delete(`/device/v1/tag/${tagId}/devices/${deviceTagId}`),
  );
};

// Delete Gateway From Tag
export const deleteGatewayTag = async (gatewayTagId, tagId) => {
  return apiSuccessOutput(
    await api.delete(`/device/v1/tag/${tagId}/gateways/${gatewayTagId}`),
  );
};

export const getMeasurementHealth = async (deviceEui, tenantId) =>
  apiSuccessOutput(
    await api.get(
      `/device/v1/measurement-health/${encodeURIComponent(deviceEui)}`,
      { tenantId, showToast: false },
    ),
  );
